import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Save, Plus, X, Building2, Phone, Mail, Calendar, IdCard, Home, Upload } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { profileService } from '../api/profile';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  Input,
  Label,
  Button,
} from '../components/ui/SharedLayout';

const Profile: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [photo, setPhoto] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [addresses, setAddresses] = useState<string[]>(['']);
  const [cpf, setCpf] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [departamentName, setDepartamentName] = useState('');
  const [managedDepartamentName, setManagedDepartamentName] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);

  const applyUserData = useCallback(() => {
    if (!user) {
      return;
    }

    setName(user.name || '');
    setEmail(user.email || '');
    setCpf(user.cpf || '');
    setPhone(user.phone || '');
    setBirthDate(user.birthDate || '');
    setAddresses(user.addresses && user.addresses.length > 0 ? user.addresses : ['']);
    setPhoto(user.photo || null);
    setDepartamentName(
      (user as { departamentName?: string | null }).departamentName ||
        (user.departamentId ? `Departamento #${user.departamentId}` : 'Não informado'),
    );
    setManagedDepartamentName(
      (user as { managedDepartamentName?: string | null }).managedDepartamentName ||
        ((user as { managedDepartamentId?: number | null }).managedDepartamentId
          ? `Departamento gerenciado #${(user as { managedDepartamentId?: number | null }).managedDepartamentId}`
          : ''),
    );
  }, [user]);

  const fetchProfile = useCallback(async () => {
    if (!user) {
      setError('Usuário não autenticado');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      applyUserData();

      try {
        const data = await profileService.getProfile();
        if (data) {
          if (data.name) setName(data.name);
          if (data.email) setEmail(data.email);
          if (data.cpf) setCpf(data.cpf);
          if (data.phone) setPhone(data.phone);
          if (data.birthDate) setBirthDate(data.birthDate);
          if (data.addresses && data.addresses.length > 0) setAddresses(data.addresses);
          if (data.photo) setPhoto(data.photo);
          setDepartamentName(
            data.departamentName ||
              (data.departamentId ? `Departamento #${data.departamentId}` : 'Não informado'),
          );
          setManagedDepartamentName(
            data.managedDepartamentName ||
              (data.managedDepartamentId
                ? `Departamento gerenciado #${data.managedDepartamentId}`
                : ''),
          );
        }
      } catch (profileError: unknown) {
        const error = profileError as {
          response?: {
            status?: number;
          };
        };
        if (error.response?.status !== 404) {
          console.error('Error fetching profile:', profileError);
        }
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Erro ao carregar o perfil');
    } finally {
      setLoading(false);
    }
  }, [applyUserData, user]);

  useEffect(() => {
    const loadData = async () => {
      try {
        applyUserData();
        if (user) {
          await fetchProfile();
        } else {
          setError('Usuário não autenticado');
        }
      } catch (error) {
        console.error('Error loading profile data:', error);
        setError('Erro ao carregar os dados do perfil');
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, [applyUserData, user, fetchProfile]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (ev) => setPhoto(ev.target?.result as string);
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleAddressChange = (idx: number, value: string) => {
    setAddresses(prev => prev.map((addr, i) => i === idx ? value : addr));
  };

  const addAddress = () => setAddresses(prev => [...prev, '']);
  const removeAddress = (idx: number) => setAddresses(prev => prev.filter((_, i) => i !== idx));

  const validateDate = (dateString: string): boolean => {
    const date = new Date(dateString);
    const minDate = new Date('1900-01-01');
    const maxDate = new Date('2100-12-31');
    return !isNaN(date.getTime()) && date >= minDate && date <= maxDate;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setError('Usuário não autenticado');
      return;
    }

    if (birthDate && !validateDate(birthDate)) {
      setError('Por favor, insira uma data de nascimento válida (entre 1900 e 2100)');
      return;
    }

    try {
      const normalizedCpf = cpf.replace(/\D/g, '');
      const normalizedPhone = phone.replace(/\D/g, '');
      await profileService.updateProfile({
        name,
        photo,
        cpf: normalizedCpf,
        birthDate,
        phone: normalizedPhone,
        email: email || user.email || '',
        addresses: addresses.filter((addr) => addr.trim() !== ''),
      });
      setSuccess('Perfil atualizado com sucesso!');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
      await fetchProfile();
    } catch (err: unknown) {
      const error = err as {
        response?: {
          data?: {
            message?: string;
          };
        };
        message?: string;
      };
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Erro ao salvar perfil. Por favor, verifique os dados e tente novamente.';
      setError(errorMessage);
      setSuccess('');
      console.error('Erro ao atualizar perfil:', err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  function formatCpf(value: string) {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      .slice(0, 14);
  }

  function formatPhone(value: string) {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .slice(0, 15);
  }

  if (loading) {
    return (
      <div className="w-full px-4 py-8 sm:px-6 lg:px-8 max-w-3xl mx-auto">
        <div className="flex h-64 items-center justify-center">
          <p className="text-slate-500">Carregando perfil...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="w-full px-4 py-8 sm:px-6 lg:px-8 max-w-3xl mx-auto">
        <PageHeader title="Perfil" description="Acesso negado — usuário não autenticado." />
        <SectionCard>
          <CardBody>
            <div className="flex flex-col items-center justify-center gap-4 p-6 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                <User className="h-7 w-7" />
              </div>
              <p className="text-slate-600">Usuário não autenticado. Faça login para acessar.</p>
              <Button variant="primary" onClick={() => navigate('/login')}>Ir para o login</Button>
            </div>
          </CardBody>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8 sm:px-6 lg:px-8 max-w-3xl mx-auto">
      <PageHeader
        title="Meu Perfil"
        description="Gerencie suas informações pessoais, foto de perfil e dados de contato."
      />

      <SectionCard>
        <CardHeader>
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-bold leading-tight text-slate-900">Dados pessoais</h2>
            <p className="text-sm text-slate-500">Atualize suas informações sempre que precisar.</p>
          </div>
        </CardHeader>
        <CardBody>
          {error && (
            <div className="mb-5 inline-flex w-full items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="mb-5 inline-flex w-full items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col items-center gap-4 rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50/70 to-white p-5">
              {photo ? (
                <img
                  src={photo}
                  alt="Foto de Perfil"
                  className="h-40 w-40 rounded-full object-cover ring-4 ring-blue-200 shadow-[0_10px_30px_-15px_rgba(37,99,235,0.45)]"
                />
              ) : (
                <div className="flex h-40 w-40 items-center justify-center rounded-full bg-gradient-to-br from-slate-100 to-slate-200 text-slate-400 ring-4 ring-slate-200 shadow-[0_10px_30px_-15px_rgba(15,23,42,0.3)]">
                  <User className="h-20 w-20 stroke-[1.6]" />
                </div>
              )}
              <div className="flex flex-col items-center gap-2">
                <Label className="font-semibold">Foto de Perfil</Label>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 shadow-[0_1px_2px_0_rgba(15,23,42,0.04)] hover:bg-slate-50">
                  <Upload className="h-4 w-4" />
                  Enviar foto
                  <input type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label htmlFor="profile-name">
                  <User className="h-4 w-4" /> Nome
                </Label>
                <Input
                  id="profile-name"
                  type="text"
                  className="mt-1.5"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Seu nome completo"
                />
              </div>

              <div>
                <Label htmlFor="profile-cpf">
                  <IdCard className="h-4 w-4" /> CPF
                </Label>
                <Input
                  id="profile-cpf"
                  type="text"
                  className="mt-1.5"
                  value={cpf}
                  onChange={e => setCpf(formatCpf(e.target.value))}
                  placeholder="000.000.000-00"
                  maxLength={14}
                />
              </div>

              <div>
                <Label htmlFor="profile-birthdate">
                  <Calendar className="h-4 w-4" /> Data de Nascimento
                </Label>
                <Input
                  id="profile-birthdate"
                  type="date"
                  className="mt-1.5"
                  value={birthDate}
                  onChange={e => setBirthDate(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="profile-dept">
                  <Building2 className="h-4 w-4" /> Departamento
                </Label>
                <Input
                  id="profile-dept"
                  type="text"
                  className="mt-1.5"
                  value={departamentName || 'Não informado'}
                  disabled
                />
                <p className="mt-1 text-xs text-slate-500">
                  O vínculo é determinado pelo backend e não é alterado aqui.
                </p>
              </div>

              {managedDepartamentName && (
                <div>
                  <Label htmlFor="profile-managed-dept">
                    <Building2 className="h-4 w-4" /> Departamento gerenciado
                  </Label>
                  <Input
                    id="profile-managed-dept"
                    type="text"
                    className="mt-1.5"
                    value={managedDepartamentName}
                    disabled
                  />
                </div>
              )}

              <div>
                <Label htmlFor="profile-phone">
                  <Phone className="h-4 w-4" /> Telefone
                </Label>
                <Input
                  id="profile-phone"
                  type="text"
                  className="mt-1.5"
                  value={phone}
                  onChange={e => setPhone(formatPhone(e.target.value))}
                  placeholder="(99) 99999-9999"
                  maxLength={15}
                />
              </div>

              <div>
                <Label htmlFor="profile-email">
                  <Mail className="h-4 w-4" /> Email
                </Label>
                <Input
                  id="profile-email"
                  type="email"
                  className="mt-1.5"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                />
              </div>
            </div>

            <div>
              <Label>
                <Home className="h-4 w-4" /> Endereços
              </Label>
              <div className="mt-1.5 space-y-2">
                {addresses.map((addr, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      type="text"
                      className="flex-1"
                      value={addr}
                      onChange={e => handleAddressChange(idx, e.target.value)}
                      placeholder={`Endereço ${idx + 1}`}
                    />
                    {addresses.length > 1 && (
                      <Button variant="danger" onClick={() => removeAddress(idx)} size="icon" title="Remover">
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addAddress}
                className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline"
              >
                <Plus className="h-4 w-4" /> Adicionar Endereço
              </button>
            </div>

            <div className="flex flex-col-reverse items-stretch justify-between gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center">
              <Button
                type="button"
                variant="outline"
                onClick={handleLogout}
                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              >
                <LogOut className="h-4 w-4" /> Sair
              </Button>
              <Button type="submit" variant="primary">
                <Save className="h-4 w-4" /> Salvar Alterações
              </Button>
            </div>
          </form>
        </CardBody>
      </SectionCard>
    </div>
  );
};

export default Profile;
